// eslint-disable-next-line max-classes-per-file
import Joi from "joi";
import { Clients } from "../contexts/AppContext";

export type ServiceFunctionContext = Clients;

type ServiceFunction<D, R> = (
  data: D,
  context: ServiceFunctionContext
) => Promise<R>;

/**
 * Tags any error that should be reported back to a user.
 * Everything else can use the standard `Error`.
 */
export class UserError extends Error {
  constructor(public code: string, message: string) {
    super(message);
  }
}

export class DataLockedError extends UserError {
  constructor(message: string) {
    super("423", message);
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Drop own keys whose value is `undefined`.
 *
 * Callable payloads do NOT go through `JSON.stringify` on the way out — the
 * Firebase SDK runs its own `encode()`, which maps every own key through
 * `encode(value)`, and `encode(undefined)` returns `null`. So `{ foo: undefined }`
 * arrives at the function as `{ foo: null }`, and a backend `z.string().optional()`
 * rejects it. Stripping here means "I didn't set this" reaches the backend as an
 * absent key, which is what every optional schema already expects.
 *
 * Only plain objects and arrays are walked; `File`, `Date`, `Timestamp`, and
 * every other class instance is passed through by reference so upload and
 * Firestore-write services keep their payloads intact. A plain object that
 * holds no `undefined` is returned by reference too — this rebuilds exactly
 * the objects it has to and leaves identity alone everywhere else.
 */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    const items = value.map((item) => stripUndefined(item));
    const changed = items.some((item, i) => item !== value[i]);
    return (changed ? items : value) as unknown as T;
  }
  if (!isPlainObject(value)) return value;

  const result: Record<string, unknown> = {};
  let changed = false;
  Object.keys(value).forEach((key) => {
    const entry = value[key];
    if (entry === undefined) {
      changed = true;
      return;
    }
    const stripped = stripUndefined(entry);
    if (stripped !== entry) changed = true;
    result[key] = stripped;
  });
  return (changed ? result : value) as T;
}

export default function serviceFunction<Data, Response>(
  schema: Joi.Schema<Data>,
  handler: ServiceFunction<Data, Response>
): ServiceFunction<Data, Response> {
  return async (data, context) => {
    const result = schema.validate(data, { stripUnknown: true });
    if (result.error) {
      throw new UserError("422", result.error.details[0].message);
    }
    const res = await handler(stripUndefined(result.value), context);
    return res;
  };
}
